import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listProjects, addFilesToProject, removeFilesFromProject } from '../../services/ProjectService.mjs';
import Chip from '../common/Chip';
import '../../css/FileProjects.css';
import { DEFAULT_ENTITY_COLOR } from '../../config/entityColors';

const SKIP = { skipGlobalErrorToast: true };

const errMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

/**
 * Which projects a file belongs to, and the controls to change that.
 *
 * Membership was previously a bulk-only action: to put one file in a project you
 * selected it in the browser and opened the bulk modal — and to see where a file
 * already belonged, there was nowhere to look at all. The details modal is where
 * someone is already asking questions about this one file, so it is where the
 * answer belongs.
 *
 * Adding is `editor`+ on the backend (`/api/projects/members/add`), which is a
 * stricter tier than uploading; a viewer sees the memberships and no buttons,
 * rather than buttons that 403.
 */
const FileProjects = ({ file, canEdit, onChanged }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);
    const [picking, setPicking] = useState(false);
    const [filter, setFilter] = useState('');
    const pickerRef = useRef(null);

    const memberOf = useMemo(
        () => (Array.isArray(file?.metadata?.projectIds) ? file.metadata.projectIds : []),
        [file],
    );

    useEffect(() => {
        let alive = true;
        listProjects()
            .then((list) => { if (alive) setProjects(Array.isArray(list) ? list : []); })
            .catch(() => { if (alive) setProjects([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!picking) return undefined;
        const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPicking(false); };
        const onKey = (e) => { if (e.key === 'Escape') setPicking(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [picking]);

    const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

    // Archived projects are hidden from the PICKER but never from the chips: a
    // file already in one still belongs to it, and hiding that would make the
    // membership look like it had been removed.
    const addable = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        return projects
            .filter((p) => p.status !== 'archived' && !memberOf.includes(p.id))
            .filter((p) => !needle || p.name.toLowerCase().includes(needle))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [projects, memberOf, filter]);

    const run = async (projectId, action) => {
        setBusyId(projectId);
        setError(null);
        try {
            await action(projectId, [file.id], SKIP);
            setPicking(false);
            setFilter('');
            // The parent re-fetches the document; `projectIds` lives on it, so
            // the chips below come back from the server rather than from an
            // optimistic guess that a failed write would leave wrong.
            await onChanged?.();
        } catch (err) {
            setError(errMessage(err, 'Could not update this file’s projects.'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="fp-root">
            <div className="fp-chips">
                {memberOf.length === 0 && !loading && (
                    <span className="fp-empty">Not in any project</span>
                )}

                {/* The file's memberships are known before the project NAMES
                    are. Drawing one placeholder per membership shows the right
                    count immediately without briefly showing raw ids as if they
                    were names. */}
                {loading && memberOf.map((id) => (
                    <span key={id} className="fp-chip-loading" aria-hidden="true" />
                ))}

                {!loading && memberOf.map((id) => {
                    const project = byId.get(id);
                    return (
                        <Chip
                            key={id}
                            color={project?.color}
                            variant="accent-subtle"
                            onRemove={canEdit && busyId !== id ? () => run(id, removeFilesFromProject) : undefined}
                            removeLabel={`Remove from ${project?.name || id}`}
                        >
                            {/* An id rather than a name means the project was
                                deleted or belongs elsewhere — say the id rather
                                than nothing, so it can be traced. */}
                            {project?.name || id}
                        </Chip>
                    );
                })}

                {canEdit && (
                    <div className="fp-picker" ref={pickerRef}>
                        <button
                            type="button"
                            className="fp-add"
                            onClick={() => setPicking((v) => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={picking}
                            disabled={loading || !!busyId}
                        >
                            + Add
                        </button>

                        {picking && (
                            <div className="fp-menu" role="listbox" aria-label="Add to project">
                                {projects.length > 8 && (
                                    <input
                                        className="fp-search"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        placeholder="Find a project…"
                                        aria-label="Find a project"
                                        autoFocus
                                    />
                                )}
                                {addable.length === 0 ? (
                                    <p className="fp-menu-empty">
                                        {projects.length === 0
                                            ? 'No projects yet — create one on the Projects page.'
                                            : 'This file is already in every project.'}
                                    </p>
                                ) : addable.map((project) => (
                                    <button
                                        key={project.id}
                                        type="button"
                                        className="fp-menu-item"
                                        role="option"
                                        aria-selected="false"
                                        disabled={busyId === project.id}
                                        onClick={() => run(project.id, addFilesToProject)}
                                    >
                                        <span className="fp-dot" style={{ background: project.color || DEFAULT_ENTITY_COLOR }} />
                                        <span className="fp-menu-name">{project.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {error && <p className="fp-error">{error}</p>}

            {/* Membership decides where this file lands on every machine synced
                to the project — worth one line, once. */}
            {memberOf.length > 0 && (
                <p className="fp-note">
                    A project’s layout rules decide where this file lands in synced folders.
                </p>
            )}
        </div>
    );
};

export default FileProjects;
